import{
  getPlayerProgress,
  listPlayerInventory,
  migrateInventory,
  reportSession,
  resolveCombatResult,
}from'./collection-api.js';

const SYNC_INTERVAL_MS=60_000;
const KILL_FLUSH_MS=2_500;
const QUEUE_VERSION=1;

let userId='';
let inventoryMigrated=false;
let progressMigrated=false;
let killQueue=[];
let sessionStartedAt=Date.now();
let pendingSession=null;
let pendingInventory=null;
let killFlushTimer=null;
let hooks={
  getInventory:()=>({}),
  applyInventory:()=>{},
  getProgress:()=>({}),
  applyProgress:()=>{},
  getRouteLevel:()=>1,
  onDrops:null,
  takeMetrics:null,
};
// P2.5: perdas de segundo plano acumuladas entre flushes.
let droppedQueueKills=0;
let timer=null;
let flushing=null;

const storageKey=()=>`idlemon-progress-sync:v${QUEUE_VERSION}:${userId}`;

function persist(){
  if(!userId)return;
  localStorage.setItem(storageKey(),JSON.stringify({
    version:QUEUE_VERSION,
    inventoryMigrated,
    progressMigrated,
    killQueue,
    sessionStartedAt,
    pendingSession,
    pendingInventory,
  }));
}

function restore(){
  try{
    const saved=JSON.parse(localStorage.getItem(storageKey())||'{}');
    inventoryMigrated=!!saved.inventoryMigrated;
    progressMigrated=!!saved.progressMigrated;
    killQueue=Array.isArray(saved.killQueue)?saved.killQueue:[];
    sessionStartedAt=Number(saved.sessionStartedAt)||Date.now();
    pendingSession=saved.pendingSession?.requestId?saved.pendingSession:null;
    pendingInventory=saved.pendingInventory?.requestId?saved.pendingInventory:null;
  }catch{
    inventoryMigrated=false;
    progressMigrated=false;
    killQueue=[];
    sessionStartedAt=Date.now();
    pendingSession=null;
    pendingInventory=null;
  }
}

export function recordServerKill(species,count=1,encounterId=null){
  if(!userId||!species)return;
  const event={
    kind:'kill',
    species:String(species).toLowerCase(),
    count:Math.max(1,Math.min(50,Math.floor(Number(count)||1))),
    at:Date.now(),
  };
  // Fase B: abate só conta com encounter_id emitido pelo servidor
  if(encounterId)event.encounter_id=String(encounterId);
  killQueue.push(event);
  // Fila generosa: com a aba oculta o flush pode ficar minutos estrangulado e
  // cortar aqui seria perder kills reais (encounter válido por 1h no servidor).
  if(killQueue.length>2000){
    droppedQueueKills+=killQueue.length-2000;
    killQueue=killQueue.slice(-2000);
  }
  persist();
  // Flush cedo para o trainer_level no servidor acompanhar a sessão
  if(encounterId&&progressMigrated){
    clearTimeout(killFlushTimer);
    killFlushTimer=setTimeout(()=>flushSessionSync().catch(()=>{}),KILL_FLUSH_MS);
  }
}

function mergeInventoryBuckets(inventory={},drops={}){
  const out={...inventory};
  for(const [slug,qty] of Object.entries(drops||{})){
    out[slug]=(out[slug]||0)+(Number(qty)||0);
  }
  return out;
}

export async function flushInventoryMigrate(){
  if(!userId||inventoryMigrated)return null;
  if(!pendingInventory){
    pendingInventory={
      requestId:crypto.randomUUID(),
      items:mergeInventoryBuckets(hooks.getInventory?.()||{}),
    };
    persist();
  }
  const response=await migrateInventory(pendingInventory.items,pendingInventory.requestId);
  inventoryMigrated=true;
  pendingInventory=null;
  persist();
  if(response?.inventory)hooks.applyInventory?.(response.inventory);
  return response;
}

async function flushSessionBatch(){
  if(!pendingSession){
    const elapsed=Math.max(0,Math.floor((Date.now()-sessionStartedAt)/1000));
    if(!killQueue.length&&elapsed<5)return null;
    pendingSession={
      requestId:crypto.randomUUID(),
      route:Math.max(1,Math.min(300,Math.trunc(Number(hooks.getRouteLevel())||1))),
      elapsedSec:elapsed,
      events:killQueue.splice(0,200),
      metrics:{...(hooks.takeMetrics?.()||{}),dropped_queue_kills:droppedQueueKills},
    };
    droppedQueueKills=0;
    sessionStartedAt=Date.now();
    persist();
  }
  try{
    const response=await reportSession({
      route:pendingSession.route,
      elapsedSec:pendingSession.elapsedSec,
      events:pendingSession.events,
      metrics:pendingSession.metrics||null,
    },pendingSession.requestId);
    pendingSession=null;
    persist();
    if(response?.progress)hooks.applyProgress?.(response.progress);
    if(response?.inventory)hooks.applyInventory?.(response.inventory);
    const dropTotal=Object.values(response?.drops||{}).reduce((sum,qty)=>sum+(Number(qty)||0),0);
    if(dropTotal>0)hooks.onDrops?.(response.drops);
    return response;
  }catch(error){
    console.warn('session sync failed',error);
    throw error;
  }
}

export async function flushSessionSync(){
  if(!userId||!progressMigrated)return null;
  if(flushing){
    const response=await flushing;
    return killQueue.length||pendingSession?flushSessionSync():response;
  }
  const run=(async()=>{
    let response=null;
    // Um lote pode terminar enquanto o worker acrescenta novos abates. Drena
    // alguns lotes consecutivos no resume/pagehide sem prender a UI num loop
    // infinito se o combate continuar produzindo eventos.
    for(let attempt=0;attempt<3;attempt++){
      response=await flushSessionBatch();
      if(!killQueue.length&&!pendingSession)break;
    }
    return response;
  })();
  flushing=run;
  try{return await run}
  finally{if(flushing===run)flushing=null}
}

export async function refreshInventory(){
  if(!userId||!inventoryMigrated)return null;
  const inventory=await listPlayerInventory();
  hooks.applyInventory?.(inventory||{});
  return inventory;
}

export async function refreshProgress(){
  if(!userId||!progressMigrated)return null;
  const progress=await getPlayerProgress();
  if(progress)hooks.applyProgress?.(progress);
  return progress;
}

export async function serverResolveCombat(mode,payload){
  if(!userId)return null;
  return resolveCombatResult({mode,payload},crypto.randomUUID());
}

export function inventorySyncEnabled(){
  return!!userId&&inventoryMigrated;
}

export function progressSyncEnabled(){
  return!!userId&&progressMigrated;
}

/**
 * Abates ainda não creditados pelo servidor (fila + lote em voo).
 * Enquanto isso for > 0 o progresso local está legitimamente à frente do
 * servidor, e uma resposta menor não pode rebaixar nível/XP na tela.
 */
export function pendingKillCount(){
  return killQueue.length+(pendingSession?.events?.length||0);
}

export async function initProgressSync({
  account,
  getInventory,
  applyInventory,
  getProgress,
  applyProgress,
  getRouteLevel,
  migrateProgressRpc,
  onDrops,
  takeMetrics,
}={}){
  userId=account?.session?.user?.id||'';
  if(!userId)return null;
  hooks={
    getInventory:getInventory||hooks.getInventory,
    applyInventory:applyInventory||hooks.applyInventory,
    getProgress:getProgress||hooks.getProgress,
    applyProgress:applyProgress||hooks.applyProgress,
    getRouteLevel:getRouteLevel||hooks.getRouteLevel,
    onDrops:onDrops||hooks.onDrops,
    takeMetrics:takeMetrics||hooks.takeMetrics,
  };
  restore();
  if(account?.profile?.inventory_migrated_at)inventoryMigrated=true;
  if(account?.profile?.progress_migrated_at)progressMigrated=true;

  if(!inventoryMigrated){
    await flushInventoryMigrate().catch(error=>console.warn('inventory migrate failed',error));
  }else{
    await refreshInventory().catch(()=>{});
  }

  if(!progressMigrated&&typeof migrateProgressRpc==='function'){
    try{
      const requestId=crypto.randomUUID();
      const response=await migrateProgressRpc(hooks.getProgress()||{},requestId);
      progressMigrated=true;
      persist();
      if(response?.progress)hooks.applyProgress?.(response.progress);
    }catch(error){
      console.warn('progress migrate failed',error);
    }
  }else if(progressMigrated){
    // Igual ao inventário: no F5 precisa puxar trainer_level do servidor
    // (o blob de cloud save não é mais a fonte de progresso).
    await refreshProgress().catch(error=>console.warn('progress refresh failed',error));
  }

  clearInterval(timer);
  timer=setInterval(()=>flushSessionSync().catch(()=>{}),SYNC_INTERVAL_MS);
  addEventListener('visibilitychange',()=>{
    if(document.visibilityState==='hidden')flushSessionSync().catch(()=>{});
  });
  addEventListener('pagehide',()=>flushSessionSync().catch(()=>{}),{once:true});

  return{
    inventoryMigrated,
    progressMigrated,
  };
}
